// MathVisualization.js
// Interactive math visualizations for concepts like functions, graphs, geometry

import React, { useState, useRef, useEffect } from 'react';
import { RotateCcw, Grid3x3 } from 'lucide-react';

const MathVisualization = ({ type, darkMode, onInteraction }) => {
  const canvasRef = useRef(null);
  const [a, setA] = useState(1);
  const [b, setB] = useState(0);
  const [c, setC] = useState(0);

  useEffect(() => {
    if (canvasRef.current) {
      drawVisualization();
    }
  }, [a, b, c, darkMode, type]);

  // Removed problematic useEffect that was causing infinite re-renders

  const drawVisualization = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.fillStyle = darkMode ? '#1f2937' : '#f9fafb';
    ctx.fillRect(0, 0, width, height);

    // Draw axes
    ctx.strokeStyle = darkMode ? '#6b7280' : '#9ca3af';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.moveTo(width / 2, 0);
    ctx.lineTo(width / 2, height);
    ctx.stroke();

    // Draw grid
    ctx.strokeStyle = darkMode ? '#374151' : '#e5e7eb';
    ctx.lineWidth = 0.5;
    for (let x = 0; x < width; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y < height; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Draw function based on type
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 3;
    ctx.beginPath();

    const scale = 40;
    const centerX = width / 2;
    const centerY = height / 2;

    switch (type) {
      case 'quadratic':
        // y = ax² + bx + c
        for (let x = -width / 2; x < width / 2; x++) {
          const realX = x / scale;
          const y = a * realX * realX + b * realX + c;
          const canvasX = centerX + x;
          const canvasY = centerY - y * scale;

          if (x === -width / 2) {
            ctx.moveTo(canvasX, canvasY);
          } else {
            ctx.lineTo(canvasX, canvasY);
          }
        }
        break;

      case 'trigonometric':
        // y = a * sin(bx + c)
        for (let x = -width / 2; x < width / 2; x++) {
          const realX = x / scale;
          const y = a * Math.sin(b * realX + c);
          const canvasX = centerX + x;
          const canvasY = centerY - y * scale;

          if (x === -width / 2) {
            ctx.moveTo(canvasX, canvasY);
          } else {
            ctx.lineTo(canvasX, canvasY);
          }
        }
        break;

      case 'linear':
        // y = ax + b
        const y1 = a * (-10) + b;
        const y2 = a * 10 + b;
        ctx.moveTo(centerX - 10 * scale, centerY - y1 * scale);
        ctx.lineTo(centerX + 10 * scale, centerY - y2 * scale);
        break;

      default:
        break;
    }

    ctx.stroke();

    // Draw equation
    ctx.fillStyle = darkMode ? '#ffffff' : '#1f2937';
    ctx.font = 'bold 18px Arial';
    let equation = '';
    if (type === 'quadratic') {
      equation = `y = ${a}x² + ${b}x + ${c}`;
    } else if (type === 'trigonometric') {
      equation = `y = ${a}sin(${b}x + ${c})`;
    } else if (type === 'linear') {
      equation = `y = ${a}x + ${b}`;
    }
    ctx.fillText(equation, 20, 30);
  };

  const reset = () => {
    setA(1);
    setB(0);
    setC(0);
  };

  const getTitle = () => {
    switch (type) {
      case 'quadratic':
        return 'Quadratic Functions: y = ax² + bx + c';
      case 'trigonometric':
        return 'Trigonometric Functions: y = a·sin(bx + c)';
      case 'linear':
        return 'Linear Functions: y = ax + b';
      default:
        return 'Math Visualization';
    }
  };

  return (
    <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-lg`}>
      <div className="mb-4 flex justify-between items-start">
        <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          {getTitle()}
        </h3>
        <button
          onClick={reset}
          className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-all`}
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      <canvas
        ref={canvasRef}
        width={700}
        height={500}
        className="w-full rounded-xl border-2 border-gray-300 dark:border-gray-600 mb-4"
      />

      <div className="space-y-3">
        <div>
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Parameter a: {a.toFixed(2)}
          </label>
          <input
            type="range"
            min="-5"
            max="5"
            step="0.1"
            value={a}
            onChange={(e) => setA(Number(e.target.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-purple-400 to-purple-600"
            style={{ WebkitAppearance: 'none', outline: 'none' }}
          />
          <div className="flex justify-between text-xs mt-1">
            <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>-5</span>
            <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>5</span>
          </div>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Parameter b: {b.toFixed(2)}
          </label>
          <input
            type="range"
            min="-5"
            max="5"
            step="0.1"
            value={b}
            onChange={(e) => setB(Number(e.target.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-pink-400 to-pink-600"
            style={{ WebkitAppearance: 'none', outline: 'none' }}
          />
          <div className="flex justify-between text-xs mt-1">
            <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>-5</span>
            <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>5</span>
          </div>
        </div>

        {type !== 'linear' && (
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Parameter c: {c.toFixed(2)}
            </label>
            <input
              type="range"
              min="-5"
              max="5"
              step="0.1"
              value={c}
              onChange={(e) => setC(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-indigo-400 to-indigo-600"
              style={{ WebkitAppearance: 'none', outline: 'none' }}
            />
            <div className="flex justify-between text-xs mt-1">
              <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>-5</span>
              <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>5</span>
            </div>
          </div>
        )}
      </div>

      <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
        <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
          💡 Key Concepts
        </h4>
        <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {type === 'quadratic' && (
            <>
              <li>• 'a' controls if parabola opens up (a&gt;0) or down (a&lt;0)</li>
              <li>• 'b' shifts the vertex horizontally</li>
              <li>• 'c' is the y-intercept (where x=0)</li>
              <li>• Vertex formula: x = -b/(2a)</li>
            </>
          )}
          {type === 'trigonometric' && (
            <>
              <li>• 'a' is the amplitude (height of wave)</li>
              <li>• 'b' affects the period (2π/b)</li>
              <li>• 'c' is the phase shift (horizontal shift)</li>
              <li>• sin(x) oscillates between -1 and 1</li>
            </>
          )}
          {type === 'linear' && (
            <>
              <li>• 'a' is the slope (rise/run)</li>
              <li>• 'b' is the y-intercept</li>
              <li>• Positive slope: line goes up</li>
              <li>• Negative slope: line goes down</li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default MathVisualization;
